import React, { useState, useEffect, useRef } from 'react';
import { X, Camera, Share2, Loader2, Wand2, Search, Check, Info, Trash2, AlertCircle } from 'lucide-react';
import { db } from '../../services/firebase';
import { collection, query, getDocs, limit } from 'firebase/firestore';

interface VirtualTryOnProps {
  onClose: () => void;
  isOpen?: boolean;
}

type OutfitSlot = 'top' | 'bottom' | 'shoes' | 'accessory';

// ✅ ADD THIS: Helper function to call GrsAi API
const callVirtualTryOnAPI = async (userPhotoBase64: string, garmentImageUrl: string) => {
  // First, fetch the garment image and convert to base64
  const garmentResponse = await fetch(garmentImageUrl);
  const garmentBlob = await garmentResponse.blob();
  
  const garmentBase64 = await new Promise<string>((resolve) => {
    const reader = new FileReader();
    reader.onloadend = () => resolve(reader.result as string);
    reader.readAsDataURL(garmentBlob);
  });

  // Your GrsAi API key (store in environment variable!)
  const API_KEY = import.meta.env.VITE_GRSAI_API_KEY || 'your-api-key-here';
  
  const response = await fetch('https://api.grsai.com/v1/nano-banana-fast/generate', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${API_KEY}`
    },
    body: JSON.stringify({
      prompt: "Virtual try-on: Put this clothing item on the person. Keep the person's face and body natural.",
      images: [userPhotoBase64, garmentBase64],
      strength: 0.8,
      num_steps: 30
    })
  });

  const data = await response.json();
  return data.output[0].url; // Returns the generated image URL
};

export const VirtualTryOnModal: React.FC<VirtualTryOnProps> = ({ onClose, isOpen }) => {
  // --- STATE ---
  const [isGenerating, setIsGenerating] = useState(false);
  const [userPhoto, setUserPhoto] = useState<string | null>(null);
  const [finalImage, setFinalImage] = useState<string | null>(null);
  const [loadingProducts, setLoadingProducts] = useState(true);
  const [allProducts, setAllProducts] = useState<any[]>([]);
  const [activeTab, setActiveTab] = useState<OutfitSlot>('top');
  const [showGuide, setShowGuide] = useState(true);
  const [error, setError] = useState<string | null>(null); // ✅ Add error state
  
  const [outfit, setOutfit] = useState<Record<OutfitSlot, any | null>>({
    top: null,
    bottom: null,
    shoes: null,
    accessory: null
  });

  const fileInputRef = useRef<HTMLInputElement>(null);

  // --- INITIALIZATION ---
  useEffect(() => {
    const fetchProducts = async () => {
      try {
        const q = query(collection(db, 'products'), limit(100));
        const snap = await getDocs(q);
        setAllProducts(snap.docs.map(d => ({ id: d.id, ...d.data() })));
      } catch (e) {
        console.error(e);
      } finally {
        setLoadingProducts(false);
      }
    };
    if (isOpen) fetchProducts();
  }, [isOpen]);

  // --- HANDLERS ---
  const handleUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        setUserPhoto(reader.result as string);
        setFinalImage(null);
        setShowGuide(false);
        setError(null); // ✅ Clear error on new upload
      };
      reader.readAsDataURL(file);
    }
  };

  const selectItem = (product: any) => {
    const cat = product.category?.toLowerCase() || '';
    let slot: OutfitSlot = 'top';
    if (cat.includes('shoe')) slot = 'shoes';
    else if (cat.includes('pant') || cat.includes('jean') || cat.includes('bottom')) slot = 'bottom';
    else if (cat.includes('access')) slot = 'accessory';
    else slot = 'top';

    setOutfit(prev => ({
      ...prev,
      [slot]: prev[slot]?.id === product.id ? null : product
    }));
    setFinalImage(null);
    setError(null); // ✅ Clear error on new selection
  };

  // ✅ FIXED: This is the main function that was broken
  const handleVisualize = async () => {
    // Get the selected item (prioritize top, then bottom)
    const selectedItem = outfit.top || outfit.bottom;
    
    if (!userPhoto) {
      setError("Upload your selfie first");
      return;
    }
    
    if (!selectedItem) {
      setError("Select a clothing item to try on");
      return;
    }

    setIsGenerating(true);
    setError(null);

    try {
      console.log('🎨 Starting virtual try-on...');
      
      // Call the API directly - no Firebase Functions needed!
      const resultUrl = await callVirtualTryOnAPI(userPhoto, selectedItem.image);
      
      console.log('✅ Success!', resultUrl);
      setFinalImage(resultUrl);
      
    } catch (err: any) {
      console.error('❌ Try-on failed:', err);
      setError(err.message || "Failed to generate. Make sure your API key is valid and images are clear.");
      
      // ✅ Fallback: Show a helpful message
      if (err.message.includes('API key')) {
        setError("API key missing or invalid. Add VITE_GRSAI_API_KEY to your .env file");
      }
    } finally {
      setIsGenerating(false);
    }
  };

  const filteredProducts = allProducts.filter(p => {
    const cat = p.category?.toLowerCase() || '';
    if (activeTab === 'shoes') return cat.includes('shoe');
    if (activeTab === 'bottom') return cat.includes('pant') || cat.includes('jean') || cat.includes('bottom');
    if (activeTab === 'accessory') return cat.includes('access');
    return !cat.includes('shoe') && !cat.includes('pant') && !cat.includes('jean') && !cat.includes('bottom') && !cat.includes('access');
  });

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[150] bg-black flex flex-col items-center justify-center animate-in fade-in duration-300">
      
      <div className="w-full max-w-md h-full flex flex-col bg-black border-x border-white/10 relative overflow-hidden">
        
        {/* ERROR DISPLAY - ✅ ADD THIS */}
        {error && (
          <div className="absolute top-16 left-4 right-4 z-[170] bg-red-500/90 text-white p-3 rounded-xl text-sm flex items-center gap-2 animate-in slide-in-from-top">
            <AlertCircle size={18} />
            <span className="flex-1">{error}</span>
            <button onClick={() => setError(null)} className="hover:opacity-70">×</button>
          </div>
        )}

        {/* SETUP GUIDE */}
        {showGuide && !userPhoto && (
          <div className="absolute inset-0 z-[160] bg-black p-8 flex flex-col justify-center animate-in slide-in-from-bottom duration-500">
            <button onClick={() => setShowGuide(false)} className="absolute top-6 right-6 text-white/30 hover:text-white"><X size={24} /></button>
            <div className="mb-10 text-center">
                <AlertCircle className="mx-auto text-white mb-4" size={32} />
                <h3 className="text-white font-black uppercase italic tracking-tighter text-2xl">Studio Setup</h3>
                <p className="text-white/40 text-[10px] uppercase font-bold tracking-[0.2em] mt-2">1 High-Res Photo Required</p>
            </div>
            <div className="space-y-8">
              <div className="flex gap-4 items-start">
                <div className="w-6 h-6 rounded-full bg-white text-black flex items-center justify-center text-[10px] font-black mt-1">01</div>
                <div>
                  <p className="text-white font-bold text-sm uppercase">Full Body Only</p>
                  <p className="text-white/40 text-[11px] leading-relaxed mt-1">Stand straight. Mirror selfies work best.</p>
                </div>
              </div>
              <div className="flex gap-4 items-start">
                <div className="w-6 h-6 rounded-full bg-white text-black flex items-center justify-center text-[10px] font-black mt-1">02</div>
                <div>
                  <p className="text-white font-bold text-sm uppercase">Clear Lighting</p>
                  <p className="text-white/40 text-[11px] leading-relaxed mt-1">Avoid heavy shadows for a cleaner drape.</p>
                </div>
              </div>
            </div>
            <button onClick={() => fileInputRef.current?.click()} className="mt-12 w-full py-4 bg-white text-black font-black uppercase tracking-widest rounded-2xl text-xs active:scale-95 transition-all shadow-xl">
              Upload Mirror Selfie
            </button>
          </div>
        )}

        {/* HEADER */}
        <div className="p-4 flex justify-between items-center bg-black border-b border-white/10 z-20">
          <button onClick={onClose} className="text-white hover:opacity-70"><X size={24} /></button>
          <div className="flex flex-col items-center">
            <h2 className="text-sm font-black text-white uppercase tracking-tighter flex items-center gap-2">
              <Wand2 size={14} className="text-white" /> Virtual Studio
            </h2>
          </div>
          <button 
            onClick={handleVisualize}
            disabled={!userPhoto || isGenerating || (!outfit.top && !outfit.bottom)}
            className="bg-white text-black px-5 py-1.5 rounded-full text-[10px] font-black uppercase tracking-widest disabled:opacity-20 active:scale-95 transition-all shadow-[0_0_15px_rgba(255,255,255,0.2)]"
          >
            {isGenerating ? <Loader2 className="animate-spin" size={14} /> : 'Visualize'}
          </button>
        </div>

        {/* TOP: CANVAS AREA */}
        <div className="flex-1 relative flex items-center justify-center bg-zinc-950 p-6 overflow-hidden">
          {userPhoto ? (
            <div className="relative h-full w-full flex items-center justify-center animate-in zoom-in-95 duration-300">
              <img 
                src={finalImage || userPhoto} 
                className={`max-h-full max-w-full rounded-2xl object-contain shadow-2xl transition-all duration-1000 ${finalImage ? 'opacity-100' : 'opacity-50 mix-blend-screen'}`} 
                alt="User Fit" 
              />
              
              {/* SLOTS INDICATOR */}
              <div className="absolute top-0 right-0 bottom-0 w-16 flex flex-col justify-center gap-2 pr-2">
                {(['accessory', 'top', 'bottom', 'shoes'] as OutfitSlot[]).map(slot => (
                  <div key={slot} className={`aspect-square w-12 rounded-xl border flex items-center justify-center overflow-hidden transition-all duration-300 ${outfit[slot] ? 'bg-white border-white scale-105 shadow-lg' : 'bg-black/40 border-white/10'}`}>
                    {outfit[slot] ? <img src={outfit[slot].image} className="w-full h-full object-cover" alt={slot}/> : <div className="text-[7px] font-bold text-white/20 uppercase">{slot}</div>}
                  </div>
                ))}
              </div>

              <button onClick={() => {setUserPhoto(null); setFinalImage(null); setError(null);}} className="absolute bottom-2 left-2 p-2 bg-black/60 text-white/40 rounded-full hover:text-white transition-all">
                <Trash2 size={16} />
              </button>
            </div>
          ) : (
            <label className="flex flex-col items-center justify-center w-full h-80 rounded-[2.5rem] border-2 border-dashed border-white/10 bg-white/5 cursor-pointer hover:bg-white/10 transition-all group">
              <Camera size={24} className="text-white/20 mb-4 group-hover:scale-110 transition-transform" />
              <p className="text-white font-black text-[10px] uppercase tracking-widest">Upload 1 Full Body Selfie</p>
              <input ref={fileInputRef} type="file" className="hidden" onChange={handleUpload} />
            </label>
          )}
        </div>

        <div className="h-3 bg-black border-y border-white/5" />

        {/* BOTTOM: PRODUCT PICKER */}
        <div className="h-[42%] bg-black flex flex-col overflow-hidden">
          <div className="flex border-b border-white/10">
            {(['top', 'bottom', 'shoes', 'accessory'] as OutfitSlot[]).map((tab) => (
              <button key={tab} onClick={() => setActiveTab(tab)} className={`flex-1 py-4 text-[10px] font-black uppercase tracking-widest transition-all ${activeTab === tab ? 'text-white bg-white/5' : 'text-white/30 hover:text-white/60'}`}>
                {tab}s
                {activeTab === tab && <div className="h-[2px] bg-white mt-1 mx-4" />}
              </button>
            ))}
          </div>

          <div className="flex-1 overflow-y-auto p-4 grid grid-cols-3 gap-3 scrollbar-hide">
            {loadingProducts ? (
              [1, 2, 3, 4, 5, 6].map(i => <div key={i} className="aspect-square bg-zinc-900 animate-pulse rounded-xl" />)
            ) : filteredProducts.map(product => {
                const isSelected = Object.values(outfit).some((f: any) => f?.id === product.id);
                return (
                  <button key={product.id} onClick={() => selectItem(product)} className={`relative aspect-square rounded-xl overflow-hidden border transition-all duration-300 ${isSelected ? 'border-white bg-white' : 'border-white/5 bg-zinc-900 hover:border-white/20'}`}>
                    <img src={product.image} className={`w-full h-full object-cover ${isSelected ? 'opacity-30' : ''}`} alt={product.name} />
                    {isSelected && <div className="absolute inset-0 flex items-center justify-center"><Check className="text-black" size={24} strokeWidth={4} /></div>}
                  </button>
                )
            })}
          </div>
        </div>

      </div>
    </div>
  );
};